import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useMeetingsStore = create(
  persist(
    (set, get) => ({
      meetings: [
        {
          id: 'MTG-001',
          title: 'Product Roadmap Review',
          description: 'Q2 product planning and feature discussions',
          organizer: 'Sarah Johnson',
          organizerEmail: 'sarah@cybertection.com',
          attendees: [
            { id: 'u1', name: 'Sarah Johnson', email: 'sarah@cybertection.com', status: 'organizer', notificationPreference: 'email' },
            { id: 'u2', name: 'Mike Chen', email: 'mike@cybertection.com', status: 'accepted', notificationPreference: 'desktop' },
            { id: 'u3', name: 'Lisa Brown', email: 'lisa@cybertection.com', status: 'pending', notificationPreference: 'both' },
          ],
          startTime: new Date(Date.now() + 2 * 24 * 60 * 60000), // 2 days from now
          endTime: new Date(Date.now() + 2 * 24 * 60 * 60000 + 60 * 60000), // 1 hour later
          duration: 60,
          location: 'Conference Room A',
          meetingType: 'in-person',
          conferenceLink: null,
          status: 'scheduled',
          recurrence: 'none',
          notesUrl: null,
          recordingUrl: null,
          relatedTickets: ['TKT-001', 'TKT-002'],
          created: new Date(Date.now() - 7 * 24 * 60 * 60000),
          updated: new Date(Date.now() - 7 * 24 * 60 * 60000),
        },
        {
          id: 'MTG-002',
          title: 'Weekly Team Standup',
          description: 'Weekly status update and blockers discussion',
          organizer: 'Mike Chen',
          organizerEmail: 'mike@cybertection.com',
          attendees: [
            { id: 'u2', name: 'Mike Chen', email: 'mike@cybertection.com', status: 'organizer', notificationPreference: 'both' },
            { id: 'u4', name: 'Emma Wilson', email: 'emma@cybertection.com', status: 'accepted', notificationPreference: 'desktop' },
            { id: 'u5', name: 'David Lee', email: 'david@cybertection.com', status: 'accepted', notificationPreference: 'email' },
          ],
          startTime: new Date(Date.now() + 1 * 24 * 60 * 60000 + 9 * 60 * 60000), // Tomorrow 9 AM
          endTime: new Date(Date.now() + 1 * 24 * 60 * 60000 + 9.5 * 60 * 60000), // 30 mins later
          duration: 30,
          location: 'Virtual',
          meetingType: 'video-conference',
          conferenceLink: 'https://meet.google.com/abc-defg-hij',
          status: 'scheduled',
          recurrence: 'weekly',
          notesUrl: null,
          recordingUrl: null,
          relatedTickets: [],
          created: new Date(Date.now() - 30 * 24 * 60 * 60000),
          updated: new Date(Date.now() - 30 * 24 * 60 * 60000),
        },
        {
          id: 'MTG-003',
          title: 'Client Call - Acme Corp',
          description: 'Quarterly business review with Acme Corporation',
          organizer: 'Sarah Johnson',
          organizerEmail: 'sarah@cybertection.com',
          attendees: [
            { id: 'u1', name: 'Sarah Johnson', email: 'sarah@cybertection.com', status: 'organizer', notificationPreference: 'both' },
            { id: 'u6', name: 'John Smith', email: 'john@acmecorp.com', status: 'accepted', notificationPreference: 'email' },
            { id: 'u7', name: 'Jane Doe', email: 'jane@acmecorp.com', status: 'accepted', notificationPreference: 'email' },
          ],
          startTime: new Date(Date.now() + 5 * 24 * 60 * 60000 + 14 * 60 * 60000), // 5 days from now 2 PM
          endTime: new Date(Date.now() + 5 * 24 * 60 * 60000 + 15 * 60 * 60000), // 1 hour later
          duration: 60,
          location: 'Virtual',
          meetingType: 'video-conference',
          conferenceLink: 'https://meet.google.com/xyz-uvwx-123',
          status: 'scheduled',
          recurrence: 'quarterly',
          notesUrl: null,
          recordingUrl: null,
          relatedTickets: ['TKT-015'],
          created: new Date(Date.now() - 14 * 24 * 60 * 60000),
          updated: new Date(Date.now() - 14 * 24 * 60 * 60000),
        },
      ],

      // Add new meeting
      addMeeting: (meetingData) => {
        set((state) => ({
          meetings: [
            ...state.meetings,
            {
              id: `MTG-${Date.now()}`,
              ...meetingData,
              status: 'scheduled',
              created: new Date(),
              updated: new Date(),
            },
          ],
        }))
      },

      // Update meeting
      updateMeeting: (meetingId, updates) => {
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === meetingId
              ? { ...m, ...updates, updated: new Date() }
              : m
          ),
        }))
      },

      // Delete meeting
      deleteMeeting: (meetingId) => {
        set((state) => ({
          meetings: state.meetings.filter((m) => m.id !== meetingId),
        }))
      },

      // Get meeting by ID
      getMeetingById: (meetingId) => {
        return get().meetings.find((m) => m.id === meetingId)
      },

      // Get all upcoming meetings
      getUpcomingMeetings: () => {
        const now = new Date()
        return get().meetings
          .filter((m) => m.startTime > now && m.status === 'scheduled')
          .sort((a, b) => a.startTime - b.startTime)
      },

      // Get meetings for today
      getTodaysMeetings: () => {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60000)

        return get().meetings.filter(
          (m) => m.startTime >= today && m.startTime < tomorrow
        )
      },

      // Get meetings for a specific attendee
      getMeetingsByAttendee: (attendeeEmail) => {
        return get().meetings.filter((m) =>
          m.attendees.some((a) => a.email === attendeeEmail)
        )
      },

      // Add attendee to meeting
      addAttendee: (meetingId, attendeeData) => {
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === meetingId
              ? {
                  ...m,
                  attendees: [
                    ...m.attendees,
                    { ...attendeeData, status: 'pending', notificationPreference: 'email' },
                  ],
                  updated: new Date(),
                }
              : m
          ),
        }))
      },

      // Update attendee status
      updateAttendeeStatus: (meetingId, attendeeId, status) => {
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === meetingId
              ? {
                  ...m,
                  attendees: m.attendees.map((a) =>
                    a.id === attendeeId ? { ...a, status } : a
                  ),
                  updated: new Date(),
                }
              : m
          ),
        }))
      },

      // Update attendee notification preference
      updateAttendeeNotification: (meetingId, attendeeId, notificationPreference) => {
        set((state) => ({
          meetings: state.meetings.map((m) =>
            m.id === meetingId
              ? {
                  ...m,
                  attendees: m.attendees.map((a) =>
                    a.id === attendeeId ? { ...a, notificationPreference } : a
                  ),
                  updated: new Date(),
                }
              : m
          ),
        }))
      },

      // Get meeting statistics
      getMeetingStats: () => {
        const meetings = get().meetings
        const now = new Date()
        const upcoming = meetings.filter((m) => m.startTime > now)
        const completed = meetings.filter((m) => m.startTime <= now)

        return {
          total: meetings.length,
          upcoming: upcoming.length,
          completed: completed.length,
          inPerson: meetings.filter((m) => m.meetingType === 'in-person').length,
          videoConference: meetings.filter((m) => m.meetingType === 'video-conference').length,
          conference: meetings.filter((m) => m.meetingType === 'conference-call').length,
          avgAttendees: meetings.length > 0 ? (meetings.reduce((sum, m) => sum + m.attendees.length, 0) / meetings.length).toFixed(1) : 0,
        }
      },

      // Get meetings by date range
      getMeetingsByDateRange: (startDate, endDate) => {
        return get().meetings.filter(
          (m) => m.startTime >= startDate && m.startTime <= endDate
        )
      },

      // Get meetings by meeting type
      getMeetingsByType: (meetingType) => {
        return get().meetings.filter((m) => m.meetingType === meetingType)
      },

      // Get reminders for a user (meetings happening soon)
      getUpcomingReminders: (hours = 24) => {
        const now = new Date()
        const futureTime = new Date(now.getTime() + hours * 60 * 60000)

        return get().meetings.filter(
          (m) => m.startTime > now && m.startTime <= futureTime && m.status === 'scheduled'
        )
      },
    }),
    {
      name: 'meetings-storage',
    }
  )
)
